import { Check, Building2, Target, Users } from "lucide-react";

import SEO from "../components/SEO";
const ABOUT_IMG = "https://images.unsplash.com/photo-1763684041948-f254c06b1a05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwZ2xvdmVzJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3NzgzMTk2OTZ8MA&ixlib=rb-4.1.0&q=85";

const REASONS = [
  "A growing range across rubber, cotton and industrial coated gloves",
  "Bulk-focused support for wholesalers, retailers and industrial buyers",
  "Practical product selection based on application and protection needs",
  "Pan-India dispatch support",
  "Backed by Arihant Welding & Safety, Nagpur",
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[#FBFCFA]">
      <SEO
        title="About RSN Gloves"
        description="Learn about RSN Gloves, our Made in India hand protection range and our focus on dependable supply for dealers, wholesalers and industrial buyers."
        path="/about"
      />
      <section className="bg-white border-b border-[#DFE7E2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="section-kicker">About RSN Gloves</div>
            <h1 className="font-display text-4xl sm:text-6xl font-black text-[#0F172A] mt-3 leading-[1]">
              Made for hands<br />that <span className="text-[#075B3A]">build India.</span>
            </h1>
            <p className="mt-5 text-lg text-[#5F6B66] max-w-xl leading-relaxed">
              RSN Gloves is building a focused hand-protection range for industrial, commercial and everyday applications.
            </p>
          </div>
          <div className="flex lg:justify-end">
            <img src="/images/brand/rsn-logo.webp" alt="RSN Gloves" className="w-full max-w-md h-auto object-contain" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-16 grid lg:grid-cols-2 gap-10 items-center">
        <img src={ABOUT_IMG} alt="Industrial work" className="w-full aspect-[4/3] object-cover rounded-xl" />
        <div>
          <div className="section-kicker">Our Story</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black text-[#0F172A] mt-2">Built around real market requirements.</h2>
          <p className="mt-4 text-[#5F6B66] leading-relaxed">
            Behind RSN Gloves stands <strong className="text-[#0F172A]">Arihant Welding & Safety</strong>, a Nagpur-based industrial-supplies business serving buyers across safety, welding and allied categories.
          </p>
          <p className="mt-3 text-[#5F6B66] leading-relaxed">
            That market experience shapes how we build the glove range: clear specifications, reliable availability, practical application guidance and a bulk-first sales approach.
          </p>
        </div>
      </section>

      <section className="bg-[#075B3A] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-16 text-center">
          <Target className="w-9 h-9 mx-auto text-[#A3E635]" strokeWidth={1.5} />
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/70 mt-5">Our Mission</div>
          <h2 className="font-display text-3xl sm:text-5xl font-black mt-3 leading-[1.05]">
            Make reliable hand protection easier to source across India.
          </h2>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-16 grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <div className="section-kicker">Why RSN Gloves</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black text-[#0F172A] mt-2">Built for B2B buying.</h2>
          <p className="text-[#5F6B66] mt-3 max-w-lg">A straightforward product range, clear specifications and direct sales support.</p>
        </div>
        <ul className="space-y-3">
          {REASONS.map((reason) => (
            <li key={reason} className="bg-white border border-[#DFE7E2] rounded-lg p-4 flex gap-3">
              <Check className="w-5 h-5 text-[#15935F] shrink-0 mt-0.5" />
              <span className="font-semibold text-[#26332D]">{reason}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-white border-y border-[#DFE7E2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-3 gap-8">
          <div>
            <div className="section-kicker">Marketed by</div>
            <h2 className="font-display text-3xl font-black text-[#0F172A] mt-2">Arihant Welding & Safety</h2>
          </div>
          <div className="bg-[#F6F9F7] border border-[#DFE7E2] rounded-lg p-5">
            <Building2 className="w-6 h-6 text-[#075B3A]" />
            <div className="text-xs uppercase tracking-[0.16em] font-bold text-[#6A776F] mt-4">Address</div>
            <p className="font-semibold text-[#0F172A] mt-2">Agrasen Chowk, Gandhibagh, Nagpur, Maharashtra – 440032</p>
          </div>
          <div className="bg-[#F6F9F7] border border-[#DFE7E2] rounded-lg p-5">
            <Users className="w-6 h-6 text-[#075B3A]" />
            <div className="text-xs uppercase tracking-[0.16em] font-bold text-[#6A776F] mt-4">Contact</div>
            <p className="font-display text-xl font-black text-[#0F172A] mt-2">Ashish Agrawal</p>
            <a href="tel:+918928341661" className="block text-sm mt-2 text-[#5F6B66] hover:text-[#075B3A]">+91 89283 41661</a>
            <a href="mailto:ashish.arihantsales@gmail.com" className="block text-sm mt-1 text-[#5F6B66] hover:text-[#075B3A] break-all">ashish.arihantsales@gmail.com</a>
          </div>
        </div>
      </section>
    </div>
  );
}
